import { useEffect, useId, useState } from 'react';
import { anioMesNegocio, formatearCOP } from '@tanteo/compartido';
import { masVendidosDelMes, type ProductoVendido } from '../lib/api';
import { cajaMesAMes, pygMes, type MesPyG } from '../lib/api-gastos';
import { saldoAlCierre, type MesDeCaja } from '../lib/caja';
import { useApp } from '../lib/contexto';
import {
  BarraDePagina,
  CampoLista,
  CeldaPlata,
  EncabezadoTabla,
  Esqueleto,
  EstadoVacio,
  FilaTabla,
  MensajeError,
  TablaAncha,
  Tarjeta,
} from '../ui/base';

const ETIQUETAS: Record<string, string> = {
  insumos: 'Insumos', fijos: 'Fijos', servicios: 'Servicios', nomina: 'Nómina', otros: 'Otros',
};

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

export function Reportes() {
  const { negocio } = useApp();
  const hoy = anioMesNegocio();
  const [anio, setAnio] = useState(hoy.anio);
  const [mes, setMes] = useState(hoy.mes);
  const [datos, setDatos] = useState<MesPyG | null>(null);
  const [masVendidos, setMasVendidos] = useState<ProductoVendido[] | null>(null);
  const [caja, setCaja] = useState<MesDeCaja[] | null>(null);
  const [error, setError] = useState('');
  const idComoLeer = useId();

  useEffect(() => {
    if (!negocio) return;
    setDatos(null);
    setMasVendidos(null);
    setCaja(null);
    setError('');
    // Un fallo se muestra como fallo: un cero aquí se leería como un mes sin ventas o una caja vacía.
    const mostrarError = (e: unknown) => setError(e instanceof Error ? e.message : 'No pudimos cargar los reportes.');
    pygMes(negocio.negocioId, anio, mes).then(setDatos).catch(mostrarError);
    masVendidosDelMes(negocio.negocioId, anio, mes).then(setMasVendidos).catch(mostrarError);
    cajaMesAMes(negocio.negocioId).then(setCaja).catch(mostrarError);
  }, [negocio, anio, mes]);

  if (!negocio) return null;
  const utilidad = (datos?.ventas ?? 0) - (datos?.gastosTotal ?? 0);
  const mesElegido = `${anio}-${String(mes).padStart(2, '0')}-01`;
  // El mes en curso y los que vienen no han cerrado: su saldo es el de hoy.
  const mesEnCurso = `${hoy.anio}-${String(hoy.mes).padStart(2, '0')}-01`;
  const saldoCaja = caja === null ? null : saldoAlCierre(caja, mesElegido);
  const nombreDelMes = (mesISO: string) => `${MESES[Number(mesISO.slice(5, 7)) - 1] ?? ''} ${mesISO.slice(0, 4)}`;

  return (
    <div className="space-y-4">
      <BarraDePagina titulo="Resultado del mes">
        <CampoLista value={mes} onChange={(e) => setMes(Number(e.target.value))} aria-label="Mes">
          {MESES.map((m, i) => (
            <option key={m} value={i + 1}>
              {m}
            </option>
          ))}
        </CampoLista>
        <CampoLista className="num" value={anio} onChange={(e) => setAnio(Number(e.target.value))} aria-label="Año">
          {[hoy.anio, hoy.anio - 1].map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </CampoLista>
      </BarraDePagina>

      <MensajeError caja e2e="reportes-error">{error}</MensajeError>

      {error ? null : datos === null || caja === null ? (
        <Esqueleto lineas={6} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-4">
          <Tarjeta>
            <p className="text-muted text-contenido font-bold uppercase">Ventas</p>
            <p className="num text-cifra font-extrabold">{formatearCOP(datos.ventas)}</p>
            <p className="text-muted text-contenido">{datos.numVentas} comprobantes</p>
          </Tarjeta>
          <Tarjeta>
            <p className="text-muted text-contenido font-bold uppercase">Gastos</p>
            <p className="num text-cifra font-extrabold">{formatearCOP(datos.gastosTotal)}</p>
            <ul className="text-muted mt-1 space-y-0.5 text-contenido">
              {datos.gastosPorCategoria.map((g) => (
                <li key={g.categoria} className="flex justify-between">
                  <span>{ETIQUETAS[g.categoria] ?? g.categoria}</span>
                  <span className="num">{formatearCOP(g.total)}</span>
                </li>
              ))}
            </ul>
          </Tarjeta>
          <Tarjeta>
            <p className="text-muted text-contenido font-bold uppercase">Utilidad bruta</p>
            <p className={`num text-cifra font-extrabold ${utilidad >= 0 ? 'text-success' : 'text-destructive'}`}>
              {formatearCOP(utilidad)}
            </p>
            <p className="text-muted text-contenido">ventas − gastos del mes (operativo, no contable)</p>
          </Tarjeta>
          <Tarjeta data-e2e="caja-general">
            <p className="text-muted text-contenido font-bold uppercase">Caja general</p>
            <p className={`num text-cifra font-extrabold ${(saldoCaja ?? 0) < 0 ? 'text-destructive' : ''}`}>
              {formatearCOP(saldoCaja ?? 0)}
            </p>
            <p className="text-muted text-contenido">
              {saldoCaja === null
                ? 'sin movimientos'
                : mesElegido >= mesEnCurso
                  ? 'acumulado a hoy'
                  : `acumulado al cierre de ${MESES[mes - 1]?.toLowerCase()}`}
            </p>
          </Tarjeta>
        </div>
      )}

      {!error && caja !== null && caja.length > 0 && (
        <Tarjeta>
          <p className="text-muted mb-2 text-contenido font-bold uppercase">Caja general mes a mes</p>
          {/* La explicación va antes de los números y en palabras, no en símbolos: el lector de pantalla
              la anuncia con la tabla por aria-describedby, y en el teléfono se acomoda al ancho de la tarjeta. */}
          <div id={idComoLeer} className="text-muted mb-3 text-contenido" data-e2e="caja-como-leer">
            <p className="font-semibold">Cómo leer esta tabla:</p>
            <ul className="list-disc space-y-0.5 pl-5">
              <li>Utilidad: lo que entró por ventas menos lo que salió en gastos ese mes.</li>
              <li>
                Saldo al cierre: lo que quedaba del mes anterior más la utilidad del mes. Es la plata que debería haber en
                la caja general; si no coincide con la plata real, falta registrar algo.
              </li>
              <li>Cada venta y cada gasto cuenta en el mes en que se pagó. Las ventas anuladas no cuentan.</li>
            </ul>
          </div>
          <TablaAncha data-e2e="caja-mes-a-mes" aria-describedby={idComoLeer}>
            <caption className="sr-only">Caja general mes a mes</caption>
            <EncabezadoTabla>
              <th scope="col" className="py-2 pr-2">Mes</th>
              <th scope="col" className="py-2 pr-2 text-right">Ingresos</th>
              <th scope="col" className="py-2 pr-2 text-right">Gastos</th>
              <th scope="col" className="py-2 pr-2 text-right">Utilidad</th>
              <th scope="col" className="py-2 pr-2 text-right">Saldo al cierre</th>
            </EncabezadoTabla>
            <tbody>
              {caja.map((m) => (
                <FilaTabla key={m.mes} className={m.mes === mesElegido ? 'bg-surface font-bold' : undefined}>
                  <td className="py-2 pr-2 font-semibold">{nombreDelMes(m.mes)}</td>
                  <CeldaPlata>{formatearCOP(m.ingresos)}</CeldaPlata>
                  <CeldaPlata>{formatearCOP(m.gastos)}</CeldaPlata>
                  <CeldaPlata className={m.utilidad < 0 ? 'text-destructive' : undefined}>{formatearCOP(m.utilidad)}</CeldaPlata>
                  <CeldaPlata fuerte>{formatearCOP(m.saldo)}</CeldaPlata>
                </FilaTabla>
              ))}
            </tbody>
          </TablaAncha>
        </Tarjeta>
      )}

      {!error && (
        <Tarjeta>
          <p className="text-muted mb-2 text-contenido font-bold uppercase">Los 20 más vendidos del mes</p>
          {masVendidos === null ? (
            <Esqueleto lineas={4} />
          ) : masVendidos.length === 0 ? (
            <EstadoVacio titulo="Sin ventas en el mes" detalle="Cuando haya comprobantes emitidos, aquí sale qué se vende más." />
          ) : (
            <TablaAncha data-e2e="mas-vendidos">
              <EncabezadoTabla>
                <th className="py-2 pr-2">Producto</th>
                <th className="py-2 pr-2">SKU</th>
                <th className="py-2 pr-2 text-right">Unidades</th>
                <th className="py-2 pr-2 text-right">Total</th>
              </EncabezadoTabla>
              <tbody>
                {masVendidos.map((p) => (
                  <FilaTabla key={p.sku}>
                    <td className="py-2 pr-2 font-semibold">{p.nombre}</td>
                    <td className="num py-2 pr-2">{p.sku}</td>
                    <CeldaPlata>{p.unidades}</CeldaPlata>
                    <CeldaPlata>{formatearCOP(p.total)}</CeldaPlata>
                  </FilaTabla>
                ))}
              </tbody>
            </TablaAncha>
          )}
        </Tarjeta>
      )}
    </div>
  );
}
